import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Modal, Segmented, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { Asset, AssetOrigin, SceneAssetItem } from '@monorepo/types';

import './assets-modal.scss';
import CurrentAsset from './tabs/current-asset';
import ManualUpload from './tabs/manual-upload';
import MyAssets from './tabs/my-assets/my-assets';
import { useVideoContext } from '../../../hooks/use-video';
import { LoadingState, useLoading } from '@monorepo/react-components';
import StockFootage from './tabs/stock-footage/stock-footage';
import { PartialAsset } from '../../../components/selectable-asset/selectable-asset';
import { useStore } from '../../../helpers/use-store';
import AIImageGenerator from './tabs/ai-image/ai-image';
import AIVideoGenerator from './tabs/ai-video/ai-video';

const { Title, Text } = Typography;

export enum AssetTab {
  Current = 'Current',
  MyAssets = 'My Assets',
  StockMedia = 'Stock Media',
  AiImages = 'AI Images',
  AiVideos = 'AI Videos',
  Manual = 'Upload Manually',
}

interface Tab {
  value: string;
  label: string;
  disabled: boolean;
}

interface Props {
  videoId?: string;
}

export interface AssetSelectionModalActions {
  open: (data: {
    assetIndex: number;
    asset: Asset | PartialAsset | null;
  }) => void;
}

const AssetSelectionModal = forwardRef<AssetSelectionModalActions, Props>(
  ({ videoId }, ref) => {
    const { t } = useTranslation();
    const { loadingState, updateLoadingState } = useLoading();

    const tabs: Tab[] = [
      {
        label: t('assetsModal.tabs.current'),
        value: AssetTab.Current,
        disabled: false,
      },
      {
        label: t('assetsModal.tabs.myAssets'),
        value: AssetTab.MyAssets,
        disabled: false,
      },
      {
        label: t('assetsModal.tabs.stockMedia'),
        value: AssetTab.StockMedia,
        disabled: false,
      },
      {
        label: t('assetsModal.tabs.aiImages'),
        value: AssetTab.AiImages,
        disabled: false,
      },
      {
        label: t('assetsModal.tabs.aiVideos'),
        value: AssetTab.AiVideos,
        disabled: false,
      },
      {
        label: t('assetsModal.tabs.manual'),
        value: AssetTab.Manual,
        disabled: false,
      },
    ];

    const getAssetTabDescription = (tab: AssetTab): string => {
      const descriptions: Record<AssetTab, string> = {
        [AssetTab.Current]: t('assetsModal.descriptions.current'),
        [AssetTab.MyAssets]: t('assetsModal.descriptions.myAssets'),
        [AssetTab.StockMedia]: t('assetsModal.descriptions.stockMedia'),
        [AssetTab.AiImages]: t('assetsModal.descriptions.aiImages'),
        [AssetTab.Manual]: t('assetsModal.descriptions.manual'),
        [AssetTab.AiVideos]: t('assetsModal.descriptions.aiVideos'),
      };
      return descriptions[tab] || t('assetsModal.defaultDescription');
    };
    const [isOpen, setIsOpen] = useState(false);
    const {
      dataStore: { assetStore },
    } = useStore();
    const [selectedTab, setSelectedTab] = useState(AssetTab.Current);
    const [selectedAsset, setSelectedAsset] = useState<
      Asset | PartialAsset | null
    >(null);
    const [selectedAssetIndex, setSelectedAssetIndex] = useState<number>(0);
    const {
      updateSceneItem,
      getSceneStartFrame,
      fetchAsset,
      selectedScene,
      assets,
    } = useVideoContext();

    const sceneId = selectedScene?._id;
    const assetItem = selectedScene?.items[selectedAssetIndex];
    const assetItemData = assetItem?.data as SceneAssetItem;

    useImperativeHandle(ref, () => ({
      open: ({ asset, assetIndex }) => {
        setSelectedAsset(asset);
        setSelectedAssetIndex(assetIndex);
        setIsOpen(true);
      },
    }));

    const handleOk = async () => {
      if (!sceneId || !videoId || !selectedAsset) {
        return;
      }

      updateLoadingState(LoadingState.Loading);

      try {
        let assetId = (selectedAsset as Asset)._id;

        if (!assetId) {
          const uploadedAsset = await assetStore.uploadByUrl({
            url: selectedAsset.url,
            type: selectedAsset.type,
            sceneId,
            videoId,
            origin: AssetOrigin.StockImages,
            prompt: selectedAsset.prompt || '',
          });

          assetId = uploadedAsset._id;
        }

        // const frame = getSceneStartFrame(sceneId);

        await updateSceneItem({
          sceneId,
          videoId,
          itemIndex: selectedAssetIndex,
          updateDto: {
            data: {
              assetId,
            },
          },
        });

        await fetchAsset(assetId);

        handleCancel();
      } catch (e) {
        console.error(`failed saving`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    const handleCancel = () => {
      setIsOpen(false);
      setSelectedTab(AssetTab.Current);
      setSelectedAsset(null);
    };

    const selectExistingAsset = (asset: Asset) => {
      if (assetItemData.assetId === asset._id) {
        setSelectedAsset(null);
        return;
      }

      setSelectedAsset(asset);
      setSelectedTab(AssetTab.Current);
    };

    const selectNonExistingAssetByUrl = (partialAsset: PartialAsset) => {
      if (selectedAsset?.url === partialAsset.url) {
        setSelectedAsset(null);
        return;
      }

      setSelectedAsset(partialAsset as Asset);
    };

    const onManualAssetsUpload = (assets: Asset | Asset[]) => {
      if (Array.isArray(assets)) {
        setSelectedTab(AssetTab.MyAssets);
      } else {
        setSelectedAsset(assets);
        setSelectedTab(AssetTab.Current);
      }
    };

    const revertToOriginalAsset = async () => {
      if (!assetItemData?.originalAssetId) {
        return;
      }

      try {
        const originalAsset = await assetStore.fetch(
          assetItemData?.originalAssetId
        );

        selectExistingAsset(originalAsset);
      } catch (e) {
        console.error(`failed reverting to original asset`, e);
      }
    };

    const nowAsset = selectedAsset;
    const videoTab = tabs.find((tab) => tab.value === AssetTab.AiVideos) as Tab;

    if (!nowAsset) {
      videoTab.disabled = true;
    } else {
      videoTab.disabled = false;
    }

    return (
      <Modal
        className="assets-modal"
        title={
          <div>
            <Title style={{ marginTop: 0, marginBottom: 0 }} level={4}>
              Assets
            </Title>
            <Text type="secondary" style={{ marginTop: 0 }}>
              {getAssetTabDescription(selectedTab)}
            </Text>
          </div>
        }
        open={isOpen}
        okText={'Save'}
        onOk={handleOk}
        okButtonProps={{
          disabled: !selectedAsset,
          loading: loadingState === LoadingState.Loading,
        }}
        onCancel={handleCancel}
        width={1000}
        style={{ overflow: 'auto' }}
      >
        <Segmented
          options={[...tabs]}
          value={selectedTab}
          onChange={(value) => setSelectedTab(value as AssetTab)}
          style={{ marginBottom: 16 }}
        />
        {selectedTab === AssetTab.Current ? (
          <CurrentAsset
            revertToOriginalAsset={revertToOriginalAsset}
            originalAssetId={assetItemData?.originalAssetId as string}
            asset={nowAsset}
          />
        ) : null}
        {selectedTab === AssetTab.Manual ? (
          <ManualUpload
            sceneId={sceneId}
            videoId={videoId}
            onUpload={onManualAssetsUpload}
          />
        ) : null}
        {selectedTab === AssetTab.MyAssets ? (
          <MyAssets
            sceneId={sceneId}
            videoId={videoId}
            selectedAsset={nowAsset}
            onSelect={selectExistingAsset}
          />
        ) : null}
        {selectedTab === AssetTab.StockMedia ? (
          <StockFootage
            onSelect={selectNonExistingAssetByUrl}
            asset={nowAsset}
          />
        ) : null}
        {selectedTab === AssetTab.AiImages && nowAsset ? (
          <AIImageGenerator
            sceneId={sceneId}
            videoId={videoId}
            onSelect={selectExistingAsset}
            asset={nowAsset}
          />
        ) : null}
        {selectedTab === AssetTab.AiVideos && nowAsset ? (
          <AIVideoGenerator
            sceneId={sceneId}
            videoId={videoId}
            onSelect={selectExistingAsset}
            asset={nowAsset}
          />
        ) : null}
      </Modal>
    );
  }
);

export default AssetSelectionModal;
